import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MAX_AUTO_TICKS } from '../services/axis-scaling';
import { GRIDLINE_STYLE } from '../services/chart-theme';
import { ZoomLatch } from '../services/zoom-latch';
import { hasUsableScale, spatialLength } from '../services/spatial-scale';
import { OVERLAY_COLORS } from '../services/chart-theme';
import {
  loadSciChart,
  BoxAnnotation,
  CursorModifier,
  EAnnotationType,
  EAutoRange,
  EAxisAlignment,
  ELabelPlacement,
  ENumericFormat,
  FastLineRenderableSeries,
  HeatmapColorMap,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  RubberBandXyZoomModifier,
  SciChartSurface,
  TSciChart,
  TextAnnotation,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
  ZoomPanModifier,
  zeroArray2D,
} from '../charts/scichart';
import { LossDataRow } from '../models/loss-data-row';
import { ProjectService } from '../services/project.service';
import {
  DEFAULT_TICK_RETENTION,
} from '../services/telemetry-ring-buffer';
import {
  SpatialWindow,
  downsamplingEnabled,
  fullResolutionParamsFor,
  sampleSpanEnd,
  visibleRangeToWindow,
  xValuesForRow,
} from '../services/telemetry-downsample';
import { ChartViewportService } from '../services/chart-viewport.service';

import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FiberFault } from '../models/fiber-fault';
import { ProjectLossData } from '../models/project-loss-data';
import { ChartCaptureService } from '../services/chart-capture.service';
import { AdaptiveResolutionService } from '../services/adaptive-resolution.service';
import {
  ReadonlyTelemetryBuffer,
  TelemetryChange,
  TelemetryStoreService,
} from '../services/telemetry-store.service';
import {
  faultSeverityColor,
  isCriticalSeverity,
  isOverlaySeverity,
  translateFaultDescription,
  SectionFaultIndex,
  sectionState,
  SCHEMATIC_NEUTRAL,
  sectionStateColor,
} from './fault-health.util';
import { closeSubscription, runTeardown } from '../services/safe-teardown';

@Component({
  selector: 'app-health',
  templateUrl: './health.component.html',
  styleUrls: ['./health.component.scss'],
})
export class HealthComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * CHART CONTAINER IDS ARE PER-INSTANCE, NOT HARDCODED.
   *
   * SciChartSurface.create() resolves its host with document.getElementById,
   * which returns the FIRST match in document order. The export prerenders a
   * SECOND copy of this component offscreen; while both copies rendered the same
   * literal id, that lookup handed the offscreen surface the MOUNTED div and
   * creating it tore down the live surface already attached there.
   *
   * The capture NAME is unchanged and is still what ChartCaptureService keys on;
   * only the DOM id is made unique.
   */
  private static nextInstanceId = 0;
  private readonly instanceSuffix = `-${++HealthComponent.nextInstanceId}`;
  readonly lossGraphId = `loss-graph${this.instanceSuffix}`;



  /**
   * True once the SciChart chunk has loaded AND the surfaces exist.
   *
   * The chart container itself is deliberately NOT *ngIf'd on this:
   * SciChartSurface.create() looks the div up by id, so it has to be in the DOM
   * before creation, not after. The flag gates a loading overlay instead, which
   * is what a user should see while a 233 kB chunk is on the wire.
   */
  chartsReady = false;

  currentTab: number = 1;
  errorMessage = '';
  lossDataLoaded: boolean = false;
  mainFaultType: number = 0;
  cmsFaultType: number = 0;

  // Temp Chart
  lossData: ProjectLossData = {} as ProjectLossData;
  /**
   * SHARED loss rows, owned by TelemetryStoreService. Indexed by ABSOLUTE tick.
   *
   * This tab no longer holds its own copy and no longer writes: the store is the
   * single writer, and every read below is unchanged because a
   * ReadonlyTelemetryBuffer offers the same reads a TelemetryRingBuffer did.
   */
  get lossDataRows(): ReadonlyTelemetryBuffer<LossDataRow> {
    return this.telemetryStore.read<LossDataRow>('loss');
  }

  /** Absolute index of the newest row already folded into the fault index. */
  private lastFoldedIndex = -1;
  private storeChangeSub?: Subscription;
  xyDataSeries: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load
  FD: FiberFault[] = [];
  FP: FiberFault[] = [];
  FM: FiberFault[] = [];
  /**
   * Persistent faults per section — what Details renders.
   *
   * Details was keyed to the CURRENT tick, so a real LWL-plug condition showed at
   * tick 0 and was gone by tick 549 because the server reports a fault on the
   * ticks where it is detected, not on every tick. The desktop shows the stable
   * fault regardless of the scrubbed tick; this holds it.
   */
  faultIndex = new SectionFaultIndex();

  /**
   * A scan blanks every section, so a mid-scan all-OK Details is CORRECT.
   *
   * Wired but never set: nothing on the wire reports a scan. Left in place, and
   * false, so the rule is implemented and one assignment turns it on once DS
   * names the field.
   */
  scanInProgress = false;

  /** Suspends the automatic re-fits while the user is zoomed. */
  private zoomLatch = new ZoomLatch();
  private programmaticRangeChange = false;
  private dblClickBound = false;

  defaultFaults: FiberFault[] = [
    {
      D: 'OK',
      FT: '0',
      P: 0,
      SI: 1,
      V: 0,
      PS: 0,
      PE: 0,
    },
  ];

  channelrange_start: number = 0;
  channelrange_end: number = 4;
  channelrange_span: number = 4;
  samplingInterval: number = 1;
  heatMaps: number[][][] = [];
  isRunning: boolean = false;

  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;

  selectedProjectSubscription?: Subscription;
  tempDataSubscription?: Subscription;
  tempRTDataSubscription?: Subscription;
  tickSubscription?: Subscription;

  faultBoxAnnotation: BoxAnnotation = {} as BoxAnnotation;
  // Loss-graph fault annotations rebuilt every tick (bands + boundary markers + dB labels).
  lossFaultAnnotations: (BoxAnnotation | VerticalLineAnnotation | TextAnnotation)[] = [];

  constructor(
    public projectService: ProjectService,
    private chartCapture: ChartCaptureService,
    private chartViewport: ChartViewportService,
    private adaptive: AdaptiveResolutionService,
    private telemetryStore: TelemetryStoreService
  ) {}


  /** Project currently selected, needed by the zoom refetch. */
  private currentProjectName = '';
  /** Debounce handle for zoom-driven refetches. */
  private zoomRefetchTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRequestedWindow: SpatialWindow | null = null;
  private zoomRefetchInFlight = false;
  private static readonly ZOOM_REFETCH_DEBOUNCE_MS = 250;

  /**
   * Natural spatial window of the loss trace. Mirror mode renders the FULL array
   * width rather than stopping at LES, so the window spans the whole trace there.
   */
  private lossWindow(): SpatialWindow | null {
    const d = this.lossData;
    if (!d) return null;
    const first = this.lossDataRows.get(this.lossDataRows.firstIndex);
    // Sample span, not array length — see the mirror bound in renderLossRow.
    const arrayLength = sampleSpanEnd(first?.LR, first?.SIX, d.LES ?? 0);
    if (d.MIR && arrayLength > 0) {
      return { start: d.LSS ?? 0, end: arrayLength };
    }
    if (!(d.LES > (d.LSS ?? 0))) return null;
    return { start: d.LSS, end: d.LES };
  }

  /**
   * Wire zoom/pan to an on-demand full-resolution refetch of the visible window.
   * Mirror-aware: the mirrored trace is drawn reversed over the full array width,
   * so a left-edge zoom maps to HIGH sample indices.
   */
  private wireZoomRefetch(xAxis: NumericAxis): void {
    xAxis.visibleRangeChanged.subscribe((args) => {
      // A PAYLOAD WITH NO USABLE SI CANNOT ARM THE LATCH. Without SI every length
      // on this axis is NaN, SciChart raises a range change off that bad state,
      // and an armed latch then buys a full-resolution refetch for a zoom nobody
      // performed. Bail before noteRangeChange, not after: arming is the
      // expensive half.
      if (!hasUsableScale(this.lossData)) {
        console.warn('ZOOM-REFETCH ignored: loss payload has no usable SI');
        return;
      }

      // Before the downsampling check: taking control of the axis is a separate
      // question from whether we refetch.
      this.zoomLatch.noteRangeChange(this.programmaticRangeChange);

      if (!downsamplingEnabled()) return;
      // Our own pins and fits must not trigger a refetch — see the temperature
      // chart's copy of this guard.
      if (this.programmaticRangeChange) return;
      const range = args?.visibleRange;
      if (!range) return;
      const first = this.lossDataRows.get(this.lossDataRows.firstIndex);
      const window = visibleRangeToWindow(range.min, range.max, {
        lss: this.lossData?.LSS ?? 0,
        les: this.lossData?.LES ?? 0,
        linerLength: (this.lossData?.CRE ?? 0) - (this.lossData?.CRB ?? 0),
        arrayLength: first?.LR?.length ?? 0,
        mirrored: this.lossData?.MIR === true,
      });
      this.chartViewport.setWindow('loss', window);
      this.scheduleZoomRefetch(window);
    });
  }

  private scheduleZoomRefetch(window: SpatialWindow): void {
    // A REFETCH IS FOR A WINDOW THE USER CHOSE TO LOOK AT.
    //
    // programmaticRangeChange alone does not enforce that. It is set around our
    // own pins and fits and cleared on setTimeout(..., 0), but SciChart raises
    // visibleRangeChanged for the first data-driven autorange AFTER that tick —
    // so the flag is already false and a plain project load schedules a
    // full-resolution refetch of the extents it just fitted. That is a second
    // isRealtime=false GET per load, on top of the backfill that produced the
    // data. The latch is the honest test: it is armed above from the same
    // event, and is true only for a real gesture.
    if (!this.zoomLatch.isZoomed) {
      console.log('ZOOM-REFETCH skipped: user has not zoomed');
      return;
    }
    if (
      this.lastRequestedWindow &&
      this.lastRequestedWindow.start === window.start &&
      this.lastRequestedWindow.end === window.end
    ) {
      return;
    }
    if (this.zoomRefetchTimer) clearTimeout(this.zoomRefetchTimer);
    this.zoomRefetchTimer = setTimeout(
      () => this.refetchWindowAtFullResolution(window),
      HealthComponent.ZOOM_REFETCH_DEBOUNCE_MS
    );
  }

  /**
   * Fetch the zoomed window at full resolution and splice the rows into the ring
   * buffer, replacing the decimated rows held for those ticks.
   */
  refetchWindowAtFullResolution(window: SpatialWindow): void {
    const params = fullResolutionParamsFor(window);
    if (!params || !this.currentProjectName || this.zoomRefetchInFlight) return;
    this.zoomRefetchInFlight = true;
    this.lastRequestedWindow = window;
    this.projectService.loadLossDataWindow(this.currentProjectName, params).subscribe({
      next: (data: ProjectLossData) => {
        this.zoomRefetchInFlight = false;
        this.applyWindowedRows(data?.LRC ?? []);
      },
      error: () => {
        this.zoomRefetchInFlight = false;
      },
    });
  }

  /**
   * Everything derived FROM the loss rows, driven by the store's notification.
   *
   * Fires strictly after the write, so this can never read a stale buffer. The
   * change kind matters: a backfill REPLACES the window, so anything accumulated
   * against the previous one has to be discarded, while an append only needs the
   * new rows folded in — and a reset to a larger row set is indistinguishable
   * from an append by shape alone, which is why the store states it.
   */
  private onLossRowsChanged(change: TelemetryChange): void {
    const rows = this.lossDataRows;

    if (change.kind === 'clear') {
      this.faultIndex.reset();
      this.lastFoldedIndex = -1;
      return;
    }

    if (change.kind === 'historical') {
      this.faultIndex.reset();
      this.faultIndex.rebuildFrom(rows.toArray(), rows.firstIndex);
      this.lastFoldedIndex = rows.nextIndex - 1;
    } else if (change.kind === 'live') {
      // Fold forward from OUR high-water mark rather than from change.fromIndex,
      // so a missed notification self-heals on the next one instead of leaving a
      // permanent hole in the fault index.
      const from = Math.max(this.lastFoldedIndex + 1, rows.firstIndex);
      for (let i = from; i < rows.nextIndex; i++) {
        const row = rows.get(i);
        if (row) {
          this.faultIndex.foldRow(i, row.FD, row.FP, row.FM);
        }
      }
      this.lastFoldedIndex = rows.nextIndex - 1;
    }

    this.refreshSectionFaults(this.tick);

    if (this.sciChartSurface && rows.nextIndex > 0) {
      const at = change.kind === 'live' ? rows.nextIndex - 1 : this.tick;
      this.updateTempChartForTick(this.sciChartSurface, at.toString());
    }
  }

  /** Replace retained rows with their full-resolution counterparts, by absolute tick. */
  applyWindowedRows(rows: readonly LossDataRow[]): number {
    let replaced = 0;
    for (let tick = 0; tick < rows.length; tick++) {
      if (this.telemetryStore.replace('loss', tick, rows[tick])) replaced++;
    }
    if (replaced > 0 && this.sciChartSurface) {
      this.updateTempChartForTick(this.sciChartSurface, this.tick.toString());
    }
    return replaced;
  }

  ngOnInit() {
    // The store registers the adaptive handle for a connected family, because
    // that handle can shrink the buffer and only the writer may do that.
    this.telemetryStore.connect('loss');
    this.telemetryStore.requireRetention('loss', 'health', DEFAULT_TICK_RETENTION);
    this.projectService.isHelpOverlayVisible = false;

    // Store wiring above stays synchronous; everything that can touch SciChart
    // moves into initialise(). See TemperatureComponent.initialise() for why.
    void this.initialise();
  }

  /**
   * Surfaces first, subscriptions second.
   *
   * SciChart is dynamically imported, so createLossGraph() after an un-awaited
   * initSciChart() would have run before its classes existed, and changed$ (a
   * ReplaySubject(1)) fires onLossRowsChanged synchronously on subscribe. The
   * replay is what makes deferring the subscription free.
   */
  private async initialise(): Promise<void> {
    try {
      await this.initSciChart();
      this.chartsReady = true;
      // Axis labels are baked in from isMetricUnit at construction. Without
      // this the cold-start path builds metric axes and then binds imperial
      // data into them — see ProjectService.whenUnitsSettled.
      await this.projectService.whenUnitsSettled();
      await this.createLossGraph();
    } catch (err) {
      console.error('Health > chart initialisation failed', err);
    }

    this.storeChangeSub = this.telemetryStore
      .changed$('loss')
      .subscribe((change) => this.onLossRowsChanged(change));
    this.setupProjectSubscription();
  }

  /** Release the loss data series before the surface that owns it. */
  private disposeLossDataSeries(): void {
    if (
      this.xyDataSeries &&
      typeof (this.xyDataSeries as XyDataSeries).delete === 'function'
    ) {
      try {
        this.xyDataSeries.delete();
      } catch {
        // already deleted
      }
    }
    this.xyDataSeries = {} as XyDataSeries;
    this.lossFaultAnnotations = [];
  }

  /**
   * TEARDOWN CANNOT BE ALLOWED TO ABORT A NAVIGATION.
   *
   * Angular calls this while DEACTIVATING the route, so anything that escapes
   * here fails the navigation and the user simply does not move — which is
   * what "clicking a tab takes me back to temperature" actually was. Worse,
   * the surfaces above are already deleted by then, so the route you are stuck
   * on is holding dead charts. See safe-teardown.ts for the full stack.
   *
   * A leaked timer is a leak. A broken tab strip is the app not working.
   */
  ngOnDestroy(): void {
    runTeardown('HealthComponent.ngOnDestroy', () => {

      this.disposeLossDataSeries();
      // Dispose of the chart surface to free up resources
      if (this.sciChartSurface) {
        this.chartCapture.unregister('healthLossGraph', this.sciChartSurface);
        this.sciChartSurface.delete();
      }
    
      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('tempDataSubscription', this.tempDataSubscription);
      closeSubscription('tempRTDataSubscription', this.tempRTDataSubscription);
      closeSubscription('tickSubscription', this.tickSubscription);

      if (this.zoomRefetchTimer) {
        clearTimeout(this.zoomRefetchTimer);
        this.zoomRefetchTimer = null;
      }
      // NOT cleared. The rows belong to the store and outlive this tab — that is
      // what stops a tab switch from throwing the data away and refetching it.
      this.storeChangeSub?.unsubscribe();
      this.telemetryStore.releaseRetention('loss', 'health');
    });
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          this.currentProjectName = projectName ?? '';
          if (projectName && projectName.length > 0) {
            console.log('Health > Project changed to:', projectName);
            console.log('Health > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Health > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateLossGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the loss graph (called when project changes)
  async recreateLossGraph() {
    console.log('Health > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    
    // Dispose of the existing chart if it exists
    if (this.sciChartSurface) {
      console.log('Health > Disposing existing chart');
      this.chartCapture.unregister('healthLossGraph', this.sciChartSurface);
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data
    this.heatMaps = [];
    this.FD = [];
    this.FP = [];
    this.FM = [];
    this.lossFaultAnnotations = [];

    // Recreate the chart
    await this.createLossGraph();
  }

  selectTab(tabNo: number) {
    this.currentTab = tabNo;
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log(this.currentTab);
  }

  async initSciChart() {
    // SciChart is dynamically imported — nothing below exists until this
    // resolves. This is the only place chart objects are constructed, so it is
    // the only place that needs to wait.
    await loadSciChart();
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Loss Graph ===========================================================================

  async createLossGraph() {
    console.log('Health > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m)' : 'Imperial (ft)');
    
    // Create the SciChartSurface in the div 'scichart-root'
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      this.lossGraphId
    );

    // Store reference to the chart surface and register for PDF export capture
    this.sciChartSurface = sciChartSurface;
    this.chartCapture.register('healthLossGraph', sciChartSurface, this.lossGraphId);

    //sciChartSurface.load

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    // TODO: Set in VericureBlazor Code but not working
    //xAxis.labelProvider.formatLabel = this.formatxAxisTemperatureGraph;

    const yAxis = new NumericAxis(wasmContext, {
      // Auto-range so the full loss curve (down to ~-3.5 dB) always fits; without
      // this the fixed range clipped the flat body off the bottom of the chart.
      autoRange: EAutoRange.Always,
      // Headroom above and below the data, so the trace is not flush against the
      // frame and the fault bands have somewhere to put their dB label. This is
      // what produces the desktop's ~0.5 / -4.0 framing from data topping out
      // near 0 and bottoming near -3.25, WITHOUT hardcoding either bound: the
      // range stays data-driven and the nice-number provider picks the labels.
      growBy: new NumberRange(0.15, 0.15),
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: 'dB',
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });
    yAxis.axisAlignment = EAxisAlignment.Left;
    yAxis.growBy = new NumberRange(0.2, 0.2);
    //xAxis.visibleRange = new NumberRange(0, 100);

    sciChartSurface.xAxes.add(xAxis);

    // Telemetry item 3: publish this chart's width and wire zoom refetch.
    this.chartViewport.setPixelWidth(
      'loss',
      document.getElementById(this.lossGraphId)?.clientWidth ?? 0
    );
    this.wireZoomRefetch(xAxis);
    sciChartSurface.yAxes.add(yAxis);
    const cursorModifier = new CursorModifier({
      crosshairStroke: '#ff6600',
      crosshairStrokeThickness: 1,
      tooltipContainerBackground: '#000',
      tooltipTextStroke: '#ff6600',
      showTooltip: true,
      axisLabelFill: '#b36200',
      axisLabelStroke: '#fff',
      //showTooltip: false,   // TODO: Not Available
      showYLine: false,
    });

    //sciChartSurface.chartModifiers.add(cursorModifier);
    //sciChartSurface.chartModifiers.add(rolloverModifier);
    //sciChartSurface.chartModifiers.add(mouseWheelZoomModifier);
    //sciChartSurface.chartModifiers.add(zoomExtentsModifier);
    //sciChartSurface.chartModifiers.add(rubberBandZoomModifier);

    // Vertical Line annotation
    const ann_t1 = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: '#7F30F3',
      strokeThickness: 3,
      x1: this.projectService.getVerticalAnnotationPosition(),
      axisLabelFill: '#7F30F3',
      axisFontSize: 14,
      isEditable: true,
      onDrag: (args) => {
        this.projectService.setVerticalAnnotationPosition(
          +ann_t1.x1.toFixed(2)
        );
      },
    });

    //sciChartSurface.annotations.add(ann_t1);

    this.rolloverModifier = new RolloverModifier();
    this.rolloverModifier.rolloverLineStroke = '#41B972';
    this.rolloverModifier.rolloverLineStrokeThickness = 2;
    this.rolloverModifier.showRolloverLine = true;
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create a line series with some data
    this.xyDataSeries = new XyDataSeries(wasmContext);

    const lineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: '#ffffff',
      strokeThickness: 2,
      dataSeries: this.xyDataSeries,
    });
    lineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    lineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    sciChartSurface.renderableSeries.add(lineSeries);

    const annt_liner_start = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: OVERLAY_COLORS.linerStroke,
      strokeThickness: OVERLAY_COLORS.linerStrokeThickness,
      x1: 0,
      axisLabelFill: OVERLAY_COLORS.linerStroke,
      axisFontSize: 14,
      isEditable: false,
      isSelected: false,
    });

    const annt_liner_end = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: OVERLAY_COLORS.linerStroke,
      strokeThickness: OVERLAY_COLORS.linerStrokeThickness,
      x1: 0,
      axisLabelFill: OVERLAY_COLORS.linerStroke,
      axisFontSize: 14,
      isEditable: false,
      isSelected: false,
    });

    sciChartSurface.annotations.add(annt_liner_start);
    sciChartSurface.annotations.add(annt_liner_end);

    this.faultBoxAnnotation = new BoxAnnotation({
      fill: '#ED9311',
      stroke: '#ED9311',
      strokeThickness: 1,
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 0,
      opacity: 0.5,
    });

    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          if (this.xyDataSeries && this.xyDataSeries.count() > 0) {
            this.xyDataSeries.clear();
          }

          this.mainFaultType = 0;
          this.cmsFaultType = 0;

          // Drop the OLD project's faults here, not on the next backfill.
          //
          // These three arrays were being reset directly while the index behind
          // them kept the previous project's open windows. Any tick emitted in
          // the gap before the new loss data lands calls refreshSectionFaults and
          // repopulates Details — and now the sidebar tint — from the project the
          // user just navigated away from. Resetting the index closes that gap;
          // the sections then render their synthetic all-clear row until real
          // data arrives.
          this.faultIndex.reset();
          this.refreshSectionFaults(this.tick);
          console.log(
            'Health > projectService.selectedProject.subscribe. proj: ' + data
          );
          // console.log(
          //   'selected proj change >> dataloaded: ' + this.tempDataLoaded
          // );
        },
        (error) => (this.errorMessage = error)
      );

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.lossData.subscribe(
      (data) => {
        // PAYLOAD SCALARS ONLY. The rows themselves are the store's business,
        // and anything derived FROM the rows now happens in onLossRowsChanged —
        // otherwise which of these two subscribers ran first would decide
        // whether the fault index saw the new rows or the old ones.
        this.lossData = data;
        this.samplingInterval = data.SI;
        this.channelrange_start = data.CRB;
        this.channelrange_end = data.CRE;
        this.channelrange_span = data.CRS;

        if (data == null) {
          return;
        } else if (data.LRC == null) {
          return;
        } else if (data.LRC.length == 0) {
          return;
        } else if (data.LRC[0].LR == null) {
          return;
        } else if (data.LRC[0].LR.length == 0) {
          return;
        }

        console.log('loss data received');

        //console.log('this.channelrange_end: ' + this.channelrange_end);

        // Guarded: a missing or zero SI made this NaN, and a NaN annotation
        // position is what put the axis into the state SciChart throws on.
        const displayedLength = spatialLength(
          (this.lossDataRows.get(this.lossDataRows.firstIndex)?.LR.length ?? 0) -
            this.lossData.LSS,
          this.lossData.SI
        );
        if (displayedLength === null) {
          console.warn('loss annotations skipped: payload has no usable SI');
          return;
        }
        console.log('Length:' + displayedLength);

        annt_liner_end.x1 = displayedLength;
        annt_liner_start.x1 =
          displayedLength - (this.lossData.CRE - this.lossData.CRB);

        // Desktop parity: label the green liner start/end markers with their distance.
        const linerUnit = this.projectService.isMetricUnit ? 'm' : 'ft';
        annt_liner_start.labelValue = `${annt_liner_start.x1.toFixed(2)}${linerUnit}`;
        annt_liner_end.labelValue = `${displayedLength.toFixed(2)}${linerUnit}`;

        this.updateTempChartForTick(sciChartSurface, '0');
        this.refitUnlessZoomed(sciChartSurface);
        // y-axis auto-ranges now (NumericAxis config); the old fixed
        // NumberRange(-1, 1) clipped the ~-3 dB loss body off-screen.
        // if (yAxis.visibleRange.max <= 0) {
        //   var newMin = yAxis.visibleRange.min;
        //   var roundedMin = Math.round(newMin);
        //   if (roundedMin > newMin) {
        //     newMin = roundedMin - 0.5;
        //   } else {
        //     newMin = roundedMin - 0.4;
        //   }

        //   // console.log(
        //   //   '2: visible range updated to 5, min:' +
        //   //     newMin +
        //   //     ' rounded:' +
        //   //     roundedMin
        //   // );

        //   var newMax = 0;
        //   if (yAxis.visibleRange.max > -0.4) {
        //     newMax = yAxis.visibleRange.max + 0.4;
        //     // console.log(
        //     //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
        //     // );
        //   }

        //   yAxis.visibleRange = new NumberRange(newMin, newMax);
        // }
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription = this.projectService.lossRTData.subscribe(
      (data) => {
        if (data == null) {
          return;
        } else if (data.LRC == null) {
          return;
        } else if (data.LRC.length == 0) {
          return;
        } else if (data.LRC[0].LR == null) {
          return;
        } else if (data.LRC[0].LR.length == 0) {
          return;
        }

        console.log('loss RT data received');

        // The store appended these rows already and told us where they landed;
        // folding and redrawing happen in onLossRowsChanged so they cannot run
        // against a buffer that has not been written yet.

        // sciChartSurface.zoomExtents();
        // if (yAxis.visibleRange.max <= 0) {
        //   var newMin = yAxis.visibleRange.min;
        //   var roundedMin = Math.round(newMin);
        //   if (roundedMin > newMin) {
        //     newMin = roundedMin - 0.5;
        //   } else {
        //     newMin = roundedMin - 0.4;
        //   }

        //   // console.log(
        //   //   '2: visible range updated to 5, min:' +
        //   //     newMin +
        //   //     ' rounded:' +
        //   //     roundedMin
        //   // );

        //   var newMax = 0;
        //   if (yAxis.visibleRange.max > -0.4) {
        //     newMax = yAxis.visibleRange.max + 0.4;
        //     // console.log(
        //     //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
        //     // );
        //   }

        //   yAxis.visibleRange = new NumberRange(newMin, newMax);
        // }
      },
      (error) => (this.errorMessage = error)
    );

    // Re-enable the time-slider -> loss/health update (mirrors temperature.component
    // ~line 604). Without this the slider is dead on the Health tabs and the loss
    // graph/details stay stuck at tick 0. Guard against leaking a prior subscription
    // when the graph is recreated (e.g. metric/imperial toggle).
    if (this.tickSubscription && typeof this.tickSubscription.unsubscribe === 'function') {
      this.tickSubscription.unsubscribe();
    }
    this.tickSubscription = this.projectService.tickPosition.subscribe(
      (data) => {
        this.updateTempChartForTick(sciChartSurface, data.toString());
        // The worst of the three: driven by the TICK, so a zoom died on the very
        // next tick rather than merely on the next poll.
        this.refitUnlessZoomed(sciChartSurface);
      },
      (error) => (this.errorMessage = error)
    );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     // console.log(
    //     //   'projectService.tickPosition.subscribe. Tick: ' + data.toString()
    //     // );

    //     this.updateTempChartForTick(sciChartSurface, data.toString());
    //     sciChartSurface.zoomExtents();

    //     // console.log(
    //     //   'min:' + yAxis.visibleRange.min + ' max:' + yAxis.visibleRange.max
    //     // );
    //     if (yAxis.visibleRange.max <= 0) {
    //       var newMin = yAxis.visibleRange.min;
    //       var roundedMin = Math.round(newMin);
    //       if (roundedMin > newMin) {
    //         newMin = roundedMin - 0.5;
    //       } else {
    //         newMin = roundedMin - 0.4;
    //       }

    //       // console.log(
    //       //   '2: visible range updated to 5, min:' +
    //       //     newMin +
    //       //     ' rounded:' +
    //       //     roundedMin
    //       // );

    //       var newMax = 0;
    //       if (yAxis.visibleRange.max > -0.4) {
    //         newMax = yAxis.visibleRange.max + 0.4;
    //         // console.log(
    //         //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
    //         // );
    //       }

    //       yAxis.visibleRange = new NumberRange(newMin, newMax);
    //     }
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    const lossHost = document.getElementById(this.lossGraphId);
    if (lossHost && !this.dblClickBound) {
      this.dblClickBound = true;
      lossHost.addEventListener('dblclick', () => this.onZoomOut && this.onZoomOut());
    }

    this.onZoomOut = () => {
      this.zoomLatch.reset();
      console.log(this.channelrange_start + '-' + this.channelrange_end);
      // xAxis.visibleRange = new NumberRange(
      //   this.channelrange_start,
      //   this.channelrange_end
      // );
      sciChartSurface.zoomExtents();
    };

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (
      (dataValue - this.channelrange_start).toFixed(2) +
      (this.projectService.isMetricUnit ? 'm' : 'ft')
    );
  }

  /**
   * Publish the persistent per-section faults to the template.
   *
   * Empty sections stay EMPTY arrays rather than an OK placeholder row: the
   * desktop omits a heading entirely when its section has no entries, and the
   * template hides a section with no rows. A fabricated 0.00 m / 0.00 dB / OK row
   * reads as a measurement that was taken and came back clean, which is a
   * stronger claim than "nothing was reported here".
   */
  /**
   * The three section states driving the schematic.
   *
   * Read from the SAME open-window fault lists as Details, the sidebar and the
   * Graph bands, so every surface agrees at every tick.
   */
  get dtsState(): string {
    return sectionState(this.FD);
  }
  get preState(): string {
    return sectionState(this.FP);
  }
  get mainState(): string {
    return sectionState(this.FM);
  }

  /** Neutral stops — white, per FiberHealth.xaml. */
  readonly schematicNeutral = SCHEMATIC_NEUTRAL;

  /**
   * The three health colours the schematic binds to, via SectionColorConverter.
   *
   * Every coloured element reads one of these; nothing is positioned by fault
   * distance. A fault at 1.8 m and one at 15 m in the same section look
   * identical, because the segment takes the section's aggregate state.
   */
  get dtsColor(): string {
    return sectionStateColor(this.dtsState);
  }
  get preColor(): string {
    return sectionStateColor(this.preState);
  }
  get mainColor(): string {
    return sectionStateColor(this.mainState);
  }

  /** zoomExtents, unless the user has taken the axes. */
  private refitUnlessZoomed(sciChartSurface: SciChartSurface): void {
    if (this.zoomLatch.isZoomed) {
      return;
    }
    this.programmaticRangeChange = true;
    try {
      sciChartSurface.zoomExtents();
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  private refreshSectionFaults(currentTick: number): void {
    this.FD = this.faultIndex.section('DTS', currentTick, this.scanInProgress);
    this.FP = this.faultIndex.section('Pre', currentTick, this.scanInProgress);
    this.FM = this.faultIndex.section('Main', currentTick, this.scanInProgress);
  }

  updateTempChartForTick(sciChartSurface: SciChartSurface, val: string) {
    let sliderVal: number = +val;
    this.tick = sliderVal;

    // Update Temp Graph
    if (this.lossDataLoaded) {
      this.xyDataSeries.clear();
      //console.log('dataLoaded:true. Cleared Series.');
    }

    // Do NOT blank the fault sections here.
    //
    // This used to reset FD/FP/FM to the OK placeholders BEFORE the early
    // returns below, so any bail-out left every section reading 0.00m / 0.00 dB
    // / OK (green) — a false all-clear on the one view whose whole job is to
    // report faults. Worse, it was invisible: the loss TRACE is drawn further
    // down from the same row, and `xyDataSeries.clear()` above is guarded by
    // `lossDataLoaded`, which the bail sets false. So the chart kept its last
    // good trace on screen while Details silently claimed OK — real dB in the
    // Graph subtab, all-green in Details, exactly as reported.
    //
    // The sections are now assigned once, at the end, from a row we actually
    // hold. On a bail the previous values stay: last-known-good beats a false OK.
    if (this.lossDataRows.totalCount == 0) {
      this.lossDataLoaded = false;
      return;
    }
    this.lossDataLoaded = true;

    // The slider tick range comes from the temperature series (TRC), which can be
    // longer than the loss rows — clamp so we never index past lossDataRows.
    if (sliderVal >= this.lossDataRows.nextIndex) {
      sliderVal = this.lossDataRows.nextIndex - 1;
    }
    if (sliderVal < 0) {
      sliderVal = 0;
    }

    // Ring buffer: the tick may have been evicted, and tick 0 is the FIRST row
    // dropped — while the initial render (`updateTempChartForTick(surface, '0')`)
    // asks for exactly tick 0. Once the adaptive cap had evicted anything, that
    // load-time render bailed every time.
    //
    // Render the NEAREST retained row instead of nothing, matching the
    // never-blank path the temperature view already uses. Slightly-stale real
    // data beats both a hole and a false OK — and nothing is requested, because
    // a reload cannot bring an evicted tick back: it refetches the whole cure and
    // keeps the same newest-N window, evicting that tick again on arrival.
    const near = this.lossDataRows.nearest(sliderVal);
    if (near == null) {
      this.lossDataLoaded = false;
      return;
    }
    sliderVal = near.index;
    const lossRow = near.row;

    // Update Faults — the backend returns the same faults in all three buckets,
    // so re-route each fault to its true section (CMS/Precarriage/Main) by its
    // description code (matches desktop / the report's MapSectionByDescription).
    // Tells us in one line whether an all-OK Details is the client dropping
    // faults or the server not sending any — the two have completely different
    // fixes and look identical on screen.
    console.log(
      `HEALTH-FAULTS tick=${sliderVal} raw FD=${lossRow.FD?.length ?? 'null'} ` +
        `FP=${lossRow.FP?.length ?? 'null'} FM=${lossRow.FM?.length ?? 'null'}`
    );

    // Details AND the left-nav tint both follow the SCRUBBED tick, through the
    // same open-window rule: a fault counts while detectionTick <= tick <
    // repairTick. Scrubbing changes both together.
    //
    // The tint used to read this tick's own DETECTIONS, which made it flicker
    // neutral on every tick where an open fault simply was not re-reported. The
    // desktop binds its sidebar to FiberStateService.State — the current OPEN
    // fault state — and stays red continuously while a fault is open.
    //
    // This does not reintroduce always-red: with no fault open at the tick the
    // sections hold only the synthetic all-clear row, whose FT is '0', so both
    // levels fall through to 0 and the nav renders plain chrome.
    this.refreshSectionFaults(sliderVal);

    // FiberBreak (FT='3') is top severity — Desktop's status border treats
    // FiberBreak|Error identically (FiberStateToBrushConverter), so it maps to
    // level 2 for the sidebar/fault-level routing.
    if (this.FM.some((c) => c.FT == '2' || c.FT == '3')) {
      this.mainFaultType = 2;
      this.projectService.setFiberFaultLevel(2);
    } else if (this.FM.some((c) => c.FT == '1')) {
      this.mainFaultType = 1;
      this.projectService.setFiberFaultLevel(1);
    } else {
      this.mainFaultType = 0;
      this.projectService.setFiberFaultLevel(0);
    }

    //var hasCMSFaults = false;
    if (this.FD.some((c) => c.FT == '2' || c.FT == '3')) {
      //hasCMSFaults = true;
      this.cmsFaultType = 2;
      this.projectService.setCmsFaultLevel(2);
    } else if (this.FD.some((c) => c.FT == '1')) {
      //hasCMSFaults = true;
      this.cmsFaultType = 1;
      this.projectService.setCmsFaultLevel(1);
    } else {
      this.cmsFaultType = 0;
      this.projectService.setCmsFaultLevel(0);
    }

    // Calculations for Displayed Points and Data -----------

    const startDataPoint = this.lossData.MIR ? this.lossData.LSS : 0;
    // Mirror mode folds the trace at the END OF THE FIBER, so this bound has to be
    // a sample index. `LR.length` is only that when the row is the whole array —
    // for a decimated or liner-trimmed row it is the point count, which would fold
    // the trace in the wrong place and mirror a shortened trace.
    const endDataPoint = this.lossData.MIR
      ? sampleSpanEnd(lossRow.LR, lossRow.SIX, this.lossData.LES)
      : this.lossData.LES;
    const displayedPoints = endDataPoint - startDataPoint;

    var xValues: number[];
    var yValues: number[];

    // X Factor = (Liner Length + Device Length?) / Data Points Count

    const scaled = spatialLength(displayedPoints, this.lossData.SI);
    if (scaled === null) {
      // Render nothing rather than an axis of NaN. The trace itself is still
      // drawn by its own path; only the distance scaling is declined.
      console.warn('loss chart: no usable SI, leaving the distance axis alone');
      return;
    }
    var displayedLength = scaled;

    console.log('Slider Val:' + sliderVal);

    console.log(
      'Total Data Points:' +
        lossRow.LR.length +
        '  LSS:' +
        startDataPoint +
        '  LES:' +
        endDataPoint
    );

    var totalLength = spatialLength(lossRow.LR.length, this.lossData.SI) ?? 0;

    console.log(
      'totalLength:' + totalLength + ' IsMirror:' + this.lossData.MIR
    );
    console.log(
      'displayedLength:' +
        displayedLength +
        '  displayedPoints:' +
        displayedPoints
    );
    const xFactor = displayedLength / displayedPoints;

    // Two row shapes (telemetry item 3):
    //  - SIX present -> the row is a WINDOW SLICE with non-uniform spacing, so x
    //    comes from the true sample index. The server emits SIX whenever
    //    downsample params were sent, including on its full-resolution branch, so
    //    SIX — not DS — is what decides how to read the array.
    //  - SIX absent  -> position IS the sample index; slice as before.
    if (lossRow.SIX && lossRow.SIX.length > 0) {
      const count = Math.min(lossRow.LR.length, lossRow.SIX.length);
      yValues = new Array<number>(count);
      for (let k = 0; k < count; k++) {
        yValues[k] = lossRow.LR[k];
      }
      xValues = xValuesForRow(lossRow.SIX, count, startDataPoint, xFactor);
    } else {
      xValues = new Array<number>(displayedPoints);
      yValues = new Array<number>(displayedPoints);
      for (let j = startDataPoint; j < endDataPoint; j++) {
        xValues[j - startDataPoint] = (j - startDataPoint) * xFactor;
        yValues[j - startDataPoint] = lossRow.LR[j];
      }
    }

    if (this.lossData.MIR) {
      // Mirror mode reverses the trace for display. Reversing the Y values keeps
      // that behaviour for both row shapes — the x positions (uniform or derived
      // from SIX) stay ascending, which is what the axis expects.
      this.xyDataSeries.appendRange(xValues, [...yValues].reverse());
    } else {
      this.xyDataSeries.appendRange(xValues, yValues);
    }

    // Desktop parity: red/orange fault bands + boundary markers + dB labels over
    // each fault's position range on the loss chart (from all sections, routed above).
    this.frameYToLinerBounds(sciChartSurface, xValues, yValues, displayedLength);

    this.updateLossFaultAnnotations(
      sciChartSurface,
      xValues,
      yValues,
      startDataPoint,
      xFactor
    );

    // if (this.FD.length > 0) {
    //   this.FD.forEach((fd, index) => {
    //     console.log('Faulttype:' + fd.FT + ' SP:' + fd.PS + ' SE:' + fd.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fd.PS],
    //       xValues[fd.PE],
    //       yValues[fd.PS],
    //       yValues[fd.PE],
    //       fd.V,
    //       fd.FT
    //     );
    //   });
    // }

    // if (this.FP.length > 0) {
    //   this.FP.forEach((fp, index) => {
    //     console.log('Faulttype:' + fp.FT + ' SP:' + fp.PS + ' SE:' + fp.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fp.PS],
    //       xValues[fp.PE],
    //       yValues[fp.PS],
    //       yValues[fp.PE],
    //       fp.V,
    //       fp.FT
    //     );
    //   });
    // }
    // if (this.FM.length > 0) {
    //   this.FM.forEach((fm, index) => {
    //     console.log('Faulttype:' + fm.FT + ' SP:' + fm.PS + ' SE:' + fm.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fm.PS],
    //       xValues[fm.PE],
    //       yValues[fm.PS],
    //       yValues[fm.PE],
    //       fm.V,
    //       fm.FT
    //     );
    //   });
    // }
  }

  // Desktop parity for the loss (Health > Graph) chart: red/orange bands + boundary
  // markers + dB labels over each fault's position range. Rebuilt every tick; the
  // green liner start/end markers are preserved (not tracked in lossFaultAnnotations).
  /**
   * Scale Y to the LINER, not to the whole trace.
   *
   * Past the end of the liner the fiber loss falls off a cliff, and that drop is
   * both far deeper than anything inside the liner and of no interest. Letting it
   * drive the range compressed the entire in-liner curve — the part being
   * inspected — into a thin band near the top. The desktop frames roughly
   * 0.5 .. -4.0 dB because it scales to the liner.
   *
   * growBy on the axis cannot express this: it pads whatever range the data
   * gives, and the problem is which data counts.
   *
   * Falls back to the axis's own auto-range whenever the liner window holds no
   * points, so an unusual project degrades to the previous behaviour instead of
   * to an empty chart.
   */
  private frameYToLinerBounds(
    sciChartSurface: SciChartSurface,
    xValues: number[],
    yValues: number[],
    displayedLength: number
  ): void {
    const yAxis = sciChartSurface.yAxes.get(0);
    if (!yAxis || this.zoomLatch.isZoomed) {
      return;
    }

    const linerEnd = displayedLength;
    const linerStart = displayedLength - (this.lossData.CRE - this.lossData.CRB);

    let min = Infinity;
    let max = -Infinity;
    for (let k = 0; k < xValues.length; k++) {
      if (xValues[k] < linerStart || xValues[k] > linerEnd) {
        continue;
      }
      if (yValues[k] < min) min = yValues[k];
      if (yValues[k] > max) max = yValues[k];
    }

    if (!isFinite(min) || !isFinite(max)) {
      yAxis.autoRange = EAutoRange.Always;
      return;
    }

    // Frame to the DATA plus the 0 dB reference, rounded outward to half a dB.
    //
    // Proportional padding was too tight: on a trace sitting between -2.95 and
    // -3.35 it produced -2.90 / -3.40, a 0.5 dB window that magnified noise into
    // apparent structure and — worse — cropped 0 dB off the top entirely. Loss is
    // read AGAINST zero, so zero has to be on the chart even when no sample comes
    // near it.
    //
    // Including 0 and rounding outward to a 0.5 boundary is what produces the
    // desktop's 0.5 / -4.0 on this project, without either bound being hardcoded:
    // a trace bottoming at -3.25 pads to -3.75 and rounds to -4.0, while the top
    // is 0 padded to 0.5.
    const PAD_DB = 0.5;
    const STEP_DB = 0.5;
    const floorTo = (v: number) => Math.floor(v / STEP_DB) * STEP_DB;
    const ceilTo = (v: number) => Math.ceil(v / STEP_DB) * STEP_DB;

    const lo = floorTo(Math.min(min, 0) - PAD_DB);
    const hi = ceilTo(Math.max(max, 0) + PAD_DB);

    yAxis.autoRange = EAutoRange.Never;
    yAxis.visibleRange = new NumberRange(lo, hi);
  }

  updateLossFaultAnnotations(
    sciChartSurface: SciChartSurface,
    xValues: number[],
    yValues: number[],
    startDataPoint: number,
    xFactor: number
  ) {
    // Clear the fault annotations we drew last tick (keeps the green liner markers).
    this.lossFaultAnnotations.forEach((ann) => {
      if (sciChartSurface.annotations.contains(ann)) {
        sciChartSurface.annotations.remove(ann);
      }
    });
    this.lossFaultAnnotations = [];

    const lastIdx = xValues.length - 1;
    if (lastIdx < 0) {
      return;
    }

    // Draw a band + boundary markers + dB label for every real fault across all
    // sections (FD/FP/FM were routed by section above; OK placeholders are skipped).
    [...this.FD, ...this.FP, ...this.FM].forEach((fault) => {
      // 4-state severity (Desktop SectionColorConverter parity): Warning, Error
      // AND FiberBreak (FT='3', previously skipped) draw a band; OK rows don't.
      if (!isOverlaySeverity(fault.FT)) {
        return; // skip OK rows
      }

      // PS/PE/P are SAMPLE INDICES; xValues is an ARRAY whose position is only
      // the sample index when the row is untrimmed and starts at 0. Indexing
      // xValues[fault.PS] was therefore displaced by startDataPoint, and on a
      // downsampled row — where position and sample index are unrelated —
      // arbitrarily wrong. Both row shapes place sample s at the same x, so
      // compute it rather than look it up.
      const xOf = (sample: number) => (sample - startDataPoint) * xFactor;

      const xStart = xOf(Math.min(fault.PS, fault.PE));
      const xStop = xOf(Math.max(fault.PS, fault.PE));

      // Value-bounded band (desktop AreaHighlight = max/min dB across the
      // region). Selected by x rather than by index, for the same reason.
      let yMax = -Infinity;
      let yMin = Infinity;
      for (let k = 0; k <= lastIdx; k++) {
        if (xValues[k] < xStart || xValues[k] > xStop) {
          continue;
        }
        if (yValues[k] > yMax) yMax = yValues[k];
        if (yValues[k] < yMin) yMin = yValues[k];
      }
      if (!isFinite(yMax) || !isFinite(yMin)) {
        return;
      }

      // Region fill is the manifest's own fault-region colour, NOT the severity
      // dot palette: FiberBreak shades RED here while its dot stays grey. The
      // alpha is already in the colour, so no additional opacity.
      const fill = isCriticalSeverity(fault.FT)
        ? OVERLAY_COLORS.faultRegionError
        : OVERLAY_COLORS.faultRegionWarning;

      // A zero-width region would render as nothing; give it one sample.
      const box = new BoxAnnotation({
        fill,
        stroke: fill,
        strokeThickness: 1,
        x1: xStart,
        x2: xStop === xStart ? xStart + xFactor : xStop,
        y1: yMax,
        y2: yMin,
      });
      sciChartSurface.annotations.add(box);
      this.lossFaultAnnotations.push(box);

      // One marker line at the fault's POSITION — the region says roughly here,
      // the line says exactly here. Previously two lines marked the region's
      // edges, which the shading already shows.
      const marker = new VerticalLineAnnotation({
        showLabel: false,
        stroke: OVERLAY_COLORS.faultMarkerLine,
        strokeThickness: 2,
        x1: xOf(fault.P),
        isEditable: false,
        isSelected: false,
      });
      sciChartSurface.annotations.add(marker);
      this.lossFaultAnnotations.push(marker);

      // dB value at the top of the band.
      const tAnn = new TextAnnotation({
        text: fault.V.toFixed(2) + ' dB',
        fontSize: 12,
        x1: xStop,
        y1: yMax,
      });
      sciChartSurface.annotations.add(tAnn);
      this.lossFaultAnnotations.push(tAnn);
    });
  }

  clearBoxAnnotations(sciChartSurface: SciChartSurface) {
    for (let a = sciChartSurface.annotations.size() - 1; a >= 0; a--) {
      if (
        sciChartSurface.annotations.get(a).type !=
        EAnnotationType.RenderContextVerticalLineAnnotation
      ) {
        sciChartSurface.annotations.removeAt(a);
      }
    }
  }

  createBoxArea(
    sciChartSurface: SciChartSurface,
    x1Val: number,
    x2Val: number,
    y1Val: number,
    y2Val: number,
    faultVal: number,
    faultType: string
  ) {
    var boxColor = 'black';
    if (faultType == '1') {
      boxColor = '#F29611';
    } else if (faultType == '2') {
      boxColor = '#F7300D';
    } else {
      return;
    }

    var ann = new BoxAnnotation({
      fill: boxColor,
      stroke: boxColor,
      strokeThickness: 1,
      x1: x1Val,
      x2: x2Val,
      y1: y1Val,
      y2: y2Val,
      opacity: 0.5,
    });
    sciChartSurface.annotations.add(ann);
    var tAnn = new TextAnnotation({
      text: faultVal.toFixed(2) + ' dB',
      fontSize: 16,
      x1: x2Val,
      y1: y1Val,
    });
    sciChartSurface.annotations.add(tAnn);
  }

  updateBoxArea(
    sciChartSurface: SciChartSurface,
    show: Boolean,
    annot: BoxAnnotation,
    x1Val: number,
    x2Val: number,
    y1Val: number,
    y2Val: number
  ) {
    if (show) {
      annot.x1 = x1Val;
      annot.x2 = x2Val;
      annot.y1 = y1Val;
      annot.y2 = y2Val;
      if (!sciChartSurface.annotations.contains(annot)) {
        sciChartSurface.annotations.add(annot);
      }
    } else {
      if (sciChartSurface.annotations.contains(annot)) {
        // `, false` IS LOAD-BEARING. This is a SHOW/HIDE toggle over an annotation
        // the CALLER owns and passes back on the next tick, so it must survive the
        // hide. SciChart 5 inverted `remove()`'s second argument
        // (3.1.348: "Defaults to false for backward compatibility";
        //  5.2.69: "Defaults to true"), so a bare remove() now delete()s the
        // annotation and the next `show` re-adds a dead object. No signature
        // changed, so nothing fails to compile — see the same note in
        // curing.component.ts.
        sciChartSurface.annotations.remove(annot, false);
      }
    }
  }

  /** Human-readable fault message (English stop-gap; see fault-health.util). */
  faultMessage(description: string): string {
    return translateFaultDescription(description);
  }

  getFaultStatusColor(faultType: string): string {
    if (faultType == '0') {
      return 'green';
    } else if (faultType == '1') {
      return 'yellow';
    } else if (faultType == '2' || faultType == '3') {
      return 'red';
    } else {
      return 'green';
    }
  }

  calculateDistance(position: number, samplingInterval: number): number {
    if (samplingInterval > 0) {
      return position * samplingInterval;
    } else {
      return position;
    }
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }
}
